import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ChevronLeft, LogOut, Copy, Check, RefreshCw, Upload, Heart, Moon, Activity, Flame, Footprints, Zap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart, Line, LineChart } from "recharts";
import { DEMO_HEALTH_DATA } from "@/lib/demoHealthData";
import HealthExplainer from "@/components/HealthExplainer";
import { Sparkles } from "lucide-react";

function MetricCard({ Icon, label, value, unit, color = "var(--brand-secondary)" }) {
  return (
    <div className="rounded-xl ink-card p-4">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{label}</div>
        <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}22`, color }}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <div className="font-display font-black text-3xl font-mono-num text-white">{value ?? "—"}</div>
        {value != null && unit && <div className="text-xs text-white/40">{unit}</div>}
      </div>
    </div>
  );
}

export default function PlayerAppleHealth() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paste, setPaste] = useState("");
  const [forceDemo, setForceDemo] = useState(false);

  const load = async () => {
    const { data } = await api.get("/apple-health/me");
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const copy = async () => {
    if (!data?.webhook_url) return;
    await navigator.clipboard.writeText(data.webhook_url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
    toast.success("Webhook URL copied");
  };

  const rotate = async () => {
    if (!window.confirm("Rotate your webhook URL? The old URL will stop working immediately.")) return;
    try {
      await api.post("/apple-health/me/rotate-token");
      await load();
      toast.success("New webhook URL generated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const upload = async () => {
    if (!paste.trim()) return;
    setBusy(true);
    try {
      const json = JSON.parse(paste);
      const { data: res } = await api.post("/apple-health/me/upload", json);
      toast.success(`Imported ${res.days_written} day(s) + ${res.workouts_written} workout(s)`);
      setPaste("");
      await load();
    } catch (err) {
      if (err instanceof SyntaxError) toast.error("Invalid JSON");
      else toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const onFile = (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => setPaste(String(e.target?.result || ""));
    reader.readAsText(f);
  };

  const handleLogout = async () => { await logout(); nav("/", { replace: true }); };

  // Last 14 days chart — fall back to demo data when the player hasn't synced yet
  // so they can see how every metric (HRV, RHR, sleep, VO2, steps, HR max,
  // workouts) feeds into the Readiness Score collectively.
  const realDays = (data?.daily || []).length;
  const realWorkouts = (data?.workouts || []).length;
  const isDemo = forceDemo || (realDays === 0 && realWorkouts === 0);
  const sourceDaily = isDemo ? DEMO_HEALTH_DATA.daily : (data?.daily || []);
  const sourceWorkouts = isDemo ? DEMO_HEALTH_DATA.workouts : (data?.workouts || []);
  const sorted = [...sourceDaily].sort((a, b) => a.date.localeCompare(b.date));
  const chart = sorted.slice(-14).map((d) => ({
    date: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    hrv: d.hrv_avg,
    resting_hr: d.resting_hr,
    sleep: d.sleep_hours,
    steps: d.steps,
  }));
  const latest = sorted[sorted.length - 1] || {};

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: "var(--ink-1)" }} data-testid="player-apple-health-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Logo size="sm" variant="lockup" linkTo="/portal" dark />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/5" data-testid="apple-health-logout-button">
            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-6 space-y-6">
        <Link to="/portal" className="inline-flex items-center text-sm text-white/50 hover:text-white">
          <ChevronLeft className="h-4 w-4" /> Back to today's log
        </Link>

        <div>
          <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Sync · Apple Watch · Wear OS · Galaxy Watch</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-1">Biometrics</h1>
          <p className="text-sm text-white/50 mt-1">Auto-sync practice biometrics from your watch — Apple Health Auto Export (iOS) or Health Connect → Webhook (Android).</p>
          <div className="mt-2">
            <Link to="/portal/livup" className="text-xs underline" style={{ color: "var(--brand-secondary)" }} data-testid="link-livup-from-apple">
              Wear a LIVUP smart ring instead? Log it here →
            </Link>
          </div>
        </div>

        {isDemo ? (
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ backgroundColor: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.35)" }}
            data-testid="health-demo-banner"
          >
            <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--brand-secondary)" }} />
            <div className="flex-1">
              <div className="font-display font-bold text-white">Sample data preview</div>
              <div className="text-xs text-white/70 mt-0.5">
                {forceDemo
                  ? "Showing two weeks of realistic Apple Health Auto Export data — flip back to your real numbers anytime."
                  : "You haven&apos;t synced a watch yet, so we&apos;re showing two weeks of realistic Apple Health Auto Export data so you can see exactly how HRV, RHR, sleep, VO₂, steps, and workouts combine into the Readiness Score below. Set up the webhook to replace this with your own numbers."}
              </div>
              {forceDemo && (
                <button onClick={() => setForceDemo(false)} className="mt-2 text-xs underline" style={{ color: "var(--brand-secondary)" }} data-testid="health-show-real-btn">
                  Show my real data
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end -mt-2">
            <button
              onClick={() => setForceDemo(true)}
              className="text-xs underline inline-flex items-center gap-1.5"
              style={{ color: "var(--brand-secondary)" }}
              data-testid="health-show-demo-btn"
            >
              <Sparkles className="h-3 w-3" /> See sample data preview
            </button>
          </div>
        )}

        {/* Latest metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard Icon={Heart} label="Resting HR" value={latest.resting_hr?.toFixed(0)} unit="bpm" color="#EF4444" />
          <MetricCard Icon={Activity} label="HRV" value={latest.hrv_avg?.toFixed(0)} unit="ms" color="#22D3EE" />
          <MetricCard Icon={Moon} label="Sleep" value={latest.sleep_hours?.toFixed(1)} unit="hrs" color="#A78BFA" />
          <MetricCard Icon={Flame} label="VO₂ Max" value={latest.vo2_max?.toFixed(1)} unit="ml/kg/min" color="#F59E0B" />
          <MetricCard Icon={Footprints} label="Steps" value={latest.steps?.toLocaleString()} color="#34D399" />
          <MetricCard Icon={Zap} label="Active kcal" value={latest.active_energy_kcal?.toFixed(0)} unit="kcal" color="#F87171" />
          <MetricCard Icon={Moon} label="REM Sleep" value={latest.sleep_rem_hours?.toFixed(1)} unit="hrs" color="#818CF8" />
          <MetricCard Icon={Heart} label="HR Max" value={latest.hr_max?.toFixed(0)} unit="bpm" color="#F472B6" />
        </div>

        {/* Charts */}
        {chart.length > 0 && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl ink-card p-5">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">HRV — 14 days</div>
              <div className="h-40 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chart}>
                    <defs>
                      <linearGradient id="hrvGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
                    <Area type="monotone" dataKey="hrv" stroke="#22D3EE" strokeWidth={2} fill="url(#hrvGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="rounded-xl ink-card p-5">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Sleep + Resting HR</div>
              <div className="h-40 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
                    <Line type="monotone" dataKey="sleep" stroke="#A78BFA" strokeWidth={2} dot={false} name="Sleep h" />
                    <Line type="monotone" dataKey="resting_hr" stroke="#EF4444" strokeWidth={2} dot={false} name="Resting HR" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* How the data combines */}
        <HealthExplainer />

        {/* Setup card */}
        <div className="rounded-xl ink-card-elevated p-6">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>Auto-sync setup</div>
          <h2 className="font-display font-bold text-xl text-white mt-1">Connect your watch</h2>

          <div className="mt-5 grid md:grid-cols-2 gap-4">
            <div className="rounded-lg p-4" style={{ backgroundColor: "var(--ink-1)", border: "1px solid var(--ink-border)" }}>
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">iPhone · Apple Watch</div>
              <div className="font-display font-bold text-white mt-1">Health Auto Export</div>
              <ol className="mt-3 space-y-1.5 text-xs text-white/70 list-decimal list-inside">
                <li>Install <a href="https://apps.apple.com/us/app/health-auto-export-json-csv/id1115567069" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5" style={{ color: "var(--brand-secondary)" }}>Health Auto Export — JSON+CSV <ExternalLink className="h-3 w-3" /></a></li>
                <li>Grant Apple Health permissions (HR, HRV, RHR, Sleep, Steps, Active Energy, VO₂, Workouts).</li>
                <li>Automations → +New → REST API. Paste the URL below. Method POST · Format JSON.</li>
                <li>Schedule daily or after-workout. Save.</li>
              </ol>
            </div>
            <div className="rounded-lg p-4" style={{ backgroundColor: "var(--ink-1)", border: "1px solid var(--ink-border)" }}>
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Android · Wear OS / Galaxy Watch</div>
              <div className="font-display font-bold text-white mt-1">Health Connect → Webhook</div>
              <ol className="mt-3 space-y-1.5 text-xs text-white/70 list-decimal list-inside">
                <li>Install <a href="https://play.google.com/store/search?q=health%20connect%20webhook" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5" style={{ color: "var(--brand-secondary)" }}>Health Connect to Webhook <ExternalLink className="h-3 w-3" /></a> (or HealthSync) from Play Store.</li>
                <li>Grant <b>Health Connect</b> read permissions for Heart Rate, HRV, Sleep, Steps, Active Calories, Exercise.</li>
                <li>Add a webhook target → paste the URL below. Method POST · Format JSON.</li>
                <li>Pick interval (recommended: hourly) and save.</li>
              </ol>
            </div>
          </div>

          <div className="mt-5">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">Your private webhook URL (works for both iOS &amp; Android)</div>
            <div className="flex gap-2">
              <code className="flex-1 px-3 py-2.5 rounded-md font-mono text-xs text-white break-all" style={{ backgroundColor: "var(--ink-0)", border: "1px solid var(--ink-border)" }} data-testid="webhook-url">
                {data?.webhook_url || "Loading..."}
              </code>
              <Button variant="outline" onClick={copy} className="border-white/15 text-white bg-white/5 hover:bg-white/10 shrink-0" data-testid="copy-webhook-button">
                {copied ? <><Check className="h-4 w-4 mr-1.5" /> Copied</> : <><Copy className="h-4 w-4 mr-1.5" /> Copy</>}
              </Button>
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-white/40">
              <div>Last sync: <span className="text-white/70 font-mono">{data?.last_sync ? new Date(data.last_sync).toLocaleString() : "Never"}</span></div>
              <button onClick={rotate} className="inline-flex items-center gap-1.5 hover:text-white" data-testid="rotate-webhook-button">
                <RefreshCw className="h-3 w-3" /> Rotate URL
              </button>
            </div>
          </div>
        </div>

        {/* Manual upload */}
        <div className="rounded-xl ink-card p-6">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "var(--brand-secondary)" }}>Manual sync</div>
          <h2 className="font-display font-bold text-xl text-white mt-1">Or paste a Health Auto Export JSON</h2>
          <p className="text-sm text-white/50 mt-1">No iPhone access right now? Export to JSON from the app and paste below.</p>

          <textarea
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder='{"data":{"metrics":[...],"workouts":[...]}}'
            rows={6}
            className="w-full mt-3 rounded-md px-3 py-2 text-xs font-mono bg-[var(--ink-1)] border border-[var(--ink-border)] text-white placeholder:text-white/30 resize-y"
            data-testid="apple-health-json-textarea"
          />
          <div className="flex items-center gap-3 mt-3">
            <Button onClick={upload} disabled={!paste || busy} className="text-white font-semibold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="apple-health-upload-button">
              <Upload className="h-4 w-4 mr-2" />{busy ? "Importing…" : "Import"}
            </Button>
            <label className="text-sm text-white/60 cursor-pointer hover:text-white">
              <input type="file" accept=".json,application/json" onChange={onFile} className="hidden" data-testid="apple-health-file-input" />
              …or pick a .json file
            </label>
          </div>
        </div>

        {/* Recent workouts */}
        {sourceWorkouts.length > 0 && (
          <div>
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
              {isDemo ? "Sample workouts" : "Recent workouts"}
            </div>
            <div className="rounded-xl ink-card overflow-hidden mt-3">
              <table className="w-full text-sm">
                <thead className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase border-b border-[var(--ink-border)]">
                  <tr>
                    <th className="text-left px-3 py-2.5">Date</th>
                    <th className="text-left px-3 py-2.5">Activity</th>
                    <th className="text-right px-3 py-2.5">Duration</th>
                    <th className="text-right px-3 py-2.5">kcal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--ink-border)] text-white/80">
                  {sourceWorkouts.map((w, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2.5 whitespace-nowrap">{w.date}</td>
                      <td className="px-3 py-2.5">{w.name}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num">{w.duration_sec ? Math.round(w.duration_sec / 60) + " min" : "—"}</td>
                      <td className="px-3 py-2.5 text-right font-mono-num text-white">{w.active_energy_kcal ? Math.round(w.active_energy_kcal) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
